/**
 * Máy in hoá đơn của THIẾT BỊ này (phản ánh 30/09). Cài đặt lưu ở trình duyệt của máy đang dùng — tablet quầy
 * nối máy in của quầy, điện thoại chủ tiệm có thể chẳng nối gì — nên không đưa lên máy chủ.
 *
 * Ba cách in:
 *   - `trinh-duyet`: hộp in của hệ điều hành. Đây là đường DUY NHẤT cho máy in WiFi / mạng: trình duyệt không mở được
 *     cổng mạng thô (9100) nên không "nối thẳng WiFi" được — máy in WiFi phải được thêm vào Windows / Android trước.
 *   - `usb`: WebUSB, gửi ESC/POS thẳng tới máy in nhiệt (Chrome trên máy tính / Android).
 *   - `bluetooth`: Web Bluetooth (BLE). "Quét" = hộp chọn thiết bị của Chrome liệt kê máy in quanh đó; chọn là ghép.
 *     Máy in Bluetooth loại cũ (SPP) trình duyệt không nối được → ghép trong cài đặt Bluetooth của máy rồi dùng `trinh-duyet`.
 *
 * Thiết bị ghép qua WebUSB / Web Bluetooth chỉ sống trong phiên trang; mở lại trang thì Chrome còn nhớ quyền
 * (`getDevices`) nên thường nối lại được không cần chọn lần nữa.
 */

import type { HoaDon } from './api.ts'
import {
  bienLaiSangEscPos,
  type DongIn,
  dungBienLai,
  dungBienLaiThu,
  inBangTrinhDuyet,
  type KhoGiay,
} from './in-hoa-don.ts'

export type KieuMayIn = 'trinh-duyet' | 'usb' | 'bluetooth'

export interface CaiDatMayIn {
  kieu: KieuMayIn
  kho: KhoGiay
  /** Tên máy in đã nối (chỉ để hiện ra; USB / Bluetooth). */
  tenThietBi: string | null
  /** Bluetooth: id thiết bị Chrome cấp, để nối lại không cần quét. */
  idThietBi: string | null
}

const KHOA = 'sapa.mayIn.v1'
const MAC_DINH: CaiDatMayIn = { kieu: 'trinh-duyet', kho: 80, tenThietBi: null, idThietBi: null }

export function docCaiDatMayIn(): CaiDatMayIn {
  try {
    const t = localStorage.getItem(KHOA)
    if (t === null) return MAC_DINH
    const j = JSON.parse(t) as Partial<CaiDatMayIn>
    return {
      kieu: j.kieu === 'usb' || j.kieu === 'bluetooth' ? j.kieu : 'trinh-duyet',
      kho: j.kho === 58 ? 58 : 80,
      tenThietBi: typeof j.tenThietBi === 'string' ? j.tenThietBi : null,
      idThietBi: typeof j.idThietBi === 'string' ? j.idThietBi : null,
    }
  } catch {
    // Trình duyệt chặn bộ nhớ (cửa sổ riêng tư): cứ dùng mặc định, in vẫn được.
    return MAC_DINH
  }
}

export function luuCaiDatMayIn(c: CaiDatMayIn): void {
  try {
    localStorage.setItem(KHOA, JSON.stringify(c))
  } catch {
    // Không lưu được thì lần sau chọn lại — không phải lỗi để báo.
  }
}

export class LoiMayIn extends Error {
  readonly loai: 'khong-ho-tro' | 'huy' | 'khong-mo-duoc' | 'khong-ghi-duoc'
  constructor(loai: LoiMayIn['loai']) {
    super(loai)
    this.loai = loai
  }
}

// ------------------------------------------------------------------ kiểu tối thiểu của WebUSB / Web Bluetooth
// TypeScript chưa có sẵn hai API này trong thư viện DOM; chỉ khai đúng phần dùng tới.

interface UsbEndpoint {
  endpointNumber: number
  direction: 'in' | 'out'
  type: 'bulk' | 'interrupt' | 'isochronous'
}
interface UsbInterface {
  interfaceNumber: number
  alternate: { endpoints: UsbEndpoint[] }
}
interface UsbThietBi {
  productName?: string
  opened: boolean
  configuration: { interfaces: UsbInterface[] } | null
  open(): Promise<void>
  selectConfiguration(n: number): Promise<void>
  claimInterface(n: number): Promise<void>
  transferOut(ep: number, data: Uint8Array): Promise<{ status: string }>
}
interface UsbApi {
  requestDevice(o: { filters: object[] }): Promise<UsbThietBi>
  getDevices(): Promise<UsbThietBi[]>
}
interface BtDac {
  properties: { write: boolean; writeWithoutResponse: boolean }
  writeValueWithoutResponse?(d: Uint8Array): Promise<void>
  writeValue(d: Uint8Array): Promise<void>
}
interface BtDichVu {
  getCharacteristics(): Promise<BtDac[]>
}
interface BtMayChu {
  getPrimaryServices(): Promise<BtDichVu[]>
}
interface BtThietBi {
  id: string
  name?: string
  gatt?: { connected: boolean; connect(): Promise<BtMayChu> }
}
interface BtApi {
  requestDevice(o: { acceptAllDevices: true; optionalServices: string[] }): Promise<BtThietBi>
  getDevices?(): Promise<BtThietBi[]>
}
type NavMoRong = Navigator & { usb?: UsbApi; bluetooth?: BtApi }
const nav = (): NavMoRong => navigator

/** Dịch vụ BLE hay gặp trên máy in nhiệt (Xprinter, MHT, Goojprt, PeriPage…): phải khai trước thì Chrome mới cho đọc. */
const DICH_VU_MAY_IN = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000fee7-0000-1000-8000-00805f9b34fb',
  '0000ae30-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
]

export function coUsb(): boolean {
  return typeof navigator !== 'undefined' && nav().usb !== undefined
}
export function coBluetooth(): boolean {
  return typeof navigator !== 'undefined' && nav().bluetooth !== undefined
}

// ------------------------------------------------------------------ USB

let usbDangDung: UsbThietBi | null = null

function timDauRaBulk(t: UsbThietBi): { giao: number; dauRa: number } | null {
  for (const g of t.configuration?.interfaces ?? []) {
    const ep = g.alternate.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk')
    if (ep !== undefined) return { giao: g.interfaceNumber, dauRa: ep.endpointNumber }
  }
  return null
}

async function moUsb(t: UsbThietBi): Promise<{ giao: number; dauRa: number }> {
  if (!t.opened) await t.open()
  if (t.configuration === null) await t.selectConfiguration(1)
  const diem = timDauRaBulk(t)
  if (diem === null) throw new LoiMayIn('khong-mo-duoc')
  await t.claimInterface(diem.giao)
  return diem
}

/** Hộp chọn thiết bị USB của Chrome. Phải gọi từ một cú bấm nút. */
export async function chonMayInUsb(): Promise<{ ten: string }> {
  const usb = nav().usb
  if (usb === undefined) throw new LoiMayIn('khong-ho-tro')
  let t: UsbThietBi
  try {
    t = await usb.requestDevice({ filters: [] })
  } catch {
    throw new LoiMayIn('huy')
  }
  try {
    await moUsb(t)
  } catch {
    throw new LoiMayIn('khong-mo-duoc')
  }
  usbDangDung = t
  return { ten: t.productName ?? 'USB' }
}

async function ghiUsb(byte: Uint8Array): Promise<void> {
  const usb = nav().usb
  if (usb === undefined) throw new LoiMayIn('khong-ho-tro')
  let t = usbDangDung
  if (t === null) {
    // Đã được cấp quyền từ lần trước → lấy lại không cần chọn.
    t = (await usb.getDevices())[0] ?? null
    if (t === null) throw new LoiMayIn('khong-mo-duoc')
  }
  let diem: { giao: number; dauRa: number }
  try {
    diem = await moUsb(t)
  } catch {
    throw new LoiMayIn('khong-mo-duoc')
  }
  usbDangDung = t
  try {
    const KHUC = 4096
    for (let i = 0; i < byte.length; i += KHUC) {
      await t.transferOut(diem.dauRa, byte.subarray(i, i + KHUC))
    }
  } catch {
    throw new LoiMayIn('khong-ghi-duoc')
  }
}

// ------------------------------------------------------------------ Bluetooth (BLE)

let btDangDung: BtThietBi | null = null

/** "Quét": Chrome mở hộp liệt kê thiết bị Bluetooth quanh máy — chọn máy in là ghép. Phải gọi từ một cú bấm nút. */
export async function quetMayInBluetooth(): Promise<{ ten: string; id: string }> {
  const bt = nav().bluetooth
  if (bt === undefined) throw new LoiMayIn('khong-ho-tro')
  let t: BtThietBi
  try {
    t = await bt.requestDevice({ acceptAllDevices: true, optionalServices: DICH_VU_MAY_IN })
  } catch {
    throw new LoiMayIn('huy')
  }
  try {
    await dacGhiCua(t) // thử nối ngay để báo sớm "không phải máy in BLE"
  } catch {
    throw new LoiMayIn('khong-mo-duoc')
  }
  btDangDung = t
  return { ten: t.name ?? 'Bluetooth', id: t.id }
}

async function dacGhiCua(t: BtThietBi): Promise<BtDac> {
  const gatt = t.gatt
  if (gatt === undefined) throw new LoiMayIn('khong-mo-duoc')
  const mayChu = await gatt.connect()
  for (const dv of await mayChu.getPrimaryServices()) {
    for (const d of await dv.getCharacteristics()) {
      if (d.properties.writeWithoutResponse || d.properties.write) return d
    }
  }
  throw new LoiMayIn('khong-mo-duoc')
}

async function ghiBluetooth(byte: Uint8Array, idDaNho: string | null): Promise<void> {
  const bt = nav().bluetooth
  if (bt === undefined) throw new LoiMayIn('khong-ho-tro')
  let t = btDangDung
  if (t === null && idDaNho !== null && bt.getDevices !== undefined) {
    t = (await bt.getDevices()).find((x) => x.id === idDaNho) ?? null
  }
  if (t === null) throw new LoiMayIn('khong-mo-duoc')
  let dac: BtDac
  try {
    dac = await dacGhiCua(t)
  } catch {
    throw new LoiMayIn('khong-mo-duoc')
  }
  btDangDung = t
  try {
    // Gói nhỏ + nghỉ giữa các gói: bộ đệm máy in BLE rất bé, đẩy dồn là rớt dòng.
    const KHUC = 100
    for (let i = 0; i < byte.length; i += KHUC) {
      const goi = byte.subarray(i, i + KHUC)
      if (dac.properties.writeWithoutResponse && dac.writeValueWithoutResponse !== undefined) {
        await dac.writeValueWithoutResponse(goi)
      } else {
        await dac.writeValue(goi)
      }
      await new Promise((xong) => setTimeout(xong, 25))
    }
  } catch {
    throw new LoiMayIn('khong-ghi-duoc')
  }
}

// ------------------------------------------------------------------ in

export async function inDong(dong: DongIn[], c: CaiDatMayIn = docCaiDatMayIn()): Promise<void> {
  if (c.kieu === 'trinh-duyet') {
    inBangTrinhDuyet(dong, c.kho)
    return
  }
  const byte = bienLaiSangEscPos(dong, c.kho)
  if (c.kieu === 'usb') await ghiUsb(byte)
  else await ghiBluetooth(byte, c.idThietBi)
}

export function inHoaDon(hd: HoaDon, tenTiem: string): Promise<void> {
  return inDong(dungBienLai(hd, tenTiem))
}

export function inThu(tenTiem: string, c: CaiDatMayIn): Promise<void> {
  return inDong(dungBienLaiThu(tenTiem), c)
}
